/**
 * Admin: Advanced pipeline controls.
 *
 * Exposes individual pipeline stage triggers for debugging and ops.
 * NOT part of the main product UI — only accessible via /admin.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../../api/client.js";
import {
  usePipeline,
  PIPELINE_STAGES,
  STAGE_LABELS,
  type PipelineStage,
} from "../../hooks/use-pipeline.js";
import { RunStatusBadge } from "../../components/run-status-badge.js";
import { WarningPanel } from "../../components/warning-panel.js";

interface AdminWebsite {
  id: string;
  url: string;
  org_id: string;
}

// ── Stage data download ──────────────────────────────────────────────────────

/**
 * Map each stage to its API path prefix and sub-data endpoints.
 *
 * `subData` items are fetched at `/:runId/<item>`.
 * `topLevel` items are fetched at `/<item>` (no runId prefix) — used for
 * endpoints like `/scores/history` that live outside a specific run.
 */
const STAGE_ENDPOINTS: Record<PipelineStage, {
  path: string;
  subData: string[];
  topLevel?: string[];
}> = {
  discovery:       { path: "discovery",       subData: ["urls?limit=500"] },
  canon:           { path: "canon",           subData: ["urls?limit=500"] },
  classification:  { path: "classification",  subData: [] },
  templates:       { path: "templates",       subData: ["detected"] },
  renders:         { path: "renders",         subData: ["pages"] },
  scans:           { path: "scans",           subData: ["pages", "findings"] },
  normalisations:  { path: "normalisations",  subData: ["groups"] },
  screenshots:     { path: "screenshots",     subData: ["results"] },
  scores:          { path: "scores",          subData: [], topLevel: ["history"] },
  reports:         { path: "reports",         subData: ["payload"] },
  statements:      { path: "statements",      subData: ["payload"], topLevel: ["list"] },
  documents:       { path: "documents",       subData: ["candidates", "findings"] },
  recurring:       { path: "recurring",       subData: ["changes", "alert"], topLevel: ["list"] },
};

async function fetchStageOutput(
  websiteId: string,
  stage: PipelineStage,
  runId: string | null,
): Promise<Record<string, unknown>> {
  const ep = STAGE_ENDPOINTS[stage];
  const output: Record<string, unknown> = {
    _stage: stage,
    _websiteId: websiteId,
    _runId: runId,
    _downloadedAt: new Date().toISOString(),
  };

  // Fetch latest run summary (always works even without a runId).
  try {
    output.summary = await apiGet(`/websites/${websiteId}/${ep.path}`);
  } catch (err) {
    output.summary = { error: err instanceof Error ? err.message : String(err) };
  }

  // Fetch top-level endpoints that don't need a runId (e.g. /scores/history).
  for (const tl of ep.topLevel ?? []) {
    const key = tl.split("?")[0];
    try {
      output[key] = await apiGet(
        `/websites/${websiteId}/${ep.path}/${tl}`,
      );
    } catch (err) {
      output[key] = { error: err instanceof Error ? err.message : String(err) };
    }
  }

  // If we have a runId, also fetch the specific run detail + all sub-data.
  const rid = runId ?? (output.summary as { id?: string })?.id;
  if (rid) {
    output._resolvedRunId = rid;

    // Fetch full run detail (every stage has /:runId returning the full row).
    try {
      output.runDetail = await apiGet(
        `/websites/${websiteId}/${ep.path}/${rid}`,
      );
    } catch (err) {
      output.runDetail = { error: err instanceof Error ? err.message : String(err) };
    }

    // Fetch each sub-data endpoint under the run.
    for (const sub of ep.subData) {
      const key = sub.split("?")[0]; // strip query params for key name
      try {
        output[key] = await apiGet(
          `/websites/${websiteId}/${ep.path}/${rid}/${sub}`,
        );
      } catch (err) {
        output[key] = { error: err instanceof Error ? err.message : String(err) };
      }
    }
  }

  return output;
}

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Download all stages at once ──────────────────────────────────────────────

async function downloadAllStages(
  websiteId: string,
  stageStates: Record<PipelineStage, { runId: string | null; status: string }>,
) {
  const allData: Record<string, unknown> = {
    _websiteId: websiteId,
    _downloadedAt: new Date().toISOString(),
  };

  // Include the overall pipeline execution summary for context.
  try {
    allData._pipelineLatest = await apiGet(
      `/websites/${websiteId}/pipeline/latest`,
    );
  } catch (err) {
    allData._pipelineLatest = { error: err instanceof Error ? err.message : String(err) };
  }

  for (const stage of PIPELINE_STAGES) {
    const s = stageStates[stage];
    if (s.status === "idle") continue;
    try {
      allData[stage] = await fetchStageOutput(websiteId, stage, s.runId);
    } catch (err) {
      allData[stage] = { error: err instanceof Error ? err.message : String(err) };
    }
  }

  downloadJson(allData, `pipeline-all-${websiteId.slice(0, 8)}.json`);
}

export function AdminPipelinePage() {
  const [websiteId, setWebsiteId] = useState("");

  const { data: websiteData } = useQuery<{ websites: AdminWebsite[]; total: number }>({
    queryKey: ["admin-websites-list"],
    queryFn: () => apiGet("/admin/websites?limit=100"),
  });
  const websites = websiteData?.websites;

  const selectedOrg = websites?.find((w) => w.id === websiteId)?.org_id;
  const pipeline = usePipeline(websiteId, selectedOrg);
  const { state: manualState } = pipeline;

  const [downloading, setDownloading] = useState<string | null>(null);

  const isAnyRunning = PIPELINE_STAGES.some(
    (s) => manualState[s].status === "running",
  );

  const stageFns: Record<
    (typeof PIPELINE_STAGES)[number],
    () => Promise<unknown>
  > = {
    discovery: pipeline.runDiscovery,
    canon: pipeline.runCanon,
    classification: pipeline.runClassification,
    templates: pipeline.runTemplates,
    renders: pipeline.runRenders,
    scans: pipeline.runScans,
    normalisations: pipeline.runNormalisations,
    screenshots: pipeline.runScreenshots,
    scores: pipeline.runScores,
    reports: pipeline.runReports,
    statements: pipeline.runStatements,
    documents: pipeline.runDocuments,
    recurring: pipeline.runRecurring,
  };

  return (
    <div className="product-page">
      <h1 className="page-title">Pipeline debug</h1>
      <p className="page-subtitle">
        Run individual pipeline stages for debugging. This is an admin tool —
        not visible to end users.
      </p>

      <section className="product-section">
        <h2 className="section-title">Select website</h2>
        <select
          value={websiteId}
          onChange={(e) => {
            setWebsiteId(e.target.value);
            pipeline.reset();
          }}
          style={{
            padding: "8px 12px",
            border: "1px solid var(--color-border)",
            borderRadius: "6px",
            fontSize: "0.875rem",
            width: "100%",
            maxWidth: "400px",
          }}
        >
          <option value="">— Choose a website —</option>
          {websites?.map((w) => (
            <option key={w.id} value={w.id}>
              {w.url} ({w.id.slice(0, 8)}…)
            </option>
          ))}
        </select>
      </section>

      {websiteId && (
        <section className="product-section">
          <h2 className="section-title">Pipeline stages</h2>
          <div className="pipeline-controls" style={{ marginBottom: "12px" }}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => void pipeline.runAll()}
              disabled={isAnyRunning}
              aria-busy={isAnyRunning}
            >
              {isAnyRunning ? "Running…" : "Run all steps"}
            </button>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={pipeline.reset}
              disabled={isAnyRunning}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn--secondary"
              disabled={downloading === "all"}
              onClick={async () => {
                setDownloading("all");
                try {
                  await downloadAllStages(websiteId, manualState);
                } finally {
                  setDownloading(null);
                }
              }}
            >
              {downloading === "all" ? "Downloading…" : "Download all output"}
            </button>
          </div>

          <div
            className="pipeline-table"
            role="list"
            aria-label="Pipeline stages"
          >
            {PIPELINE_STAGES.map((stage) => {
              const s = manualState[stage];
              return (
                <div key={stage} className="pipeline-row" role="listitem">
                  <div className="pipeline-row__label">
                    {STAGE_LABELS[stage]}
                  </div>
                  <div className="pipeline-row__status">
                    <RunStatusBadge status={s.status} />
                  </div>
                  {s.runId && (
                    <div className="pipeline-row__run-id text-muted text-sm">
                      Run: {s.runId.slice(0, 8)}…
                    </div>
                  )}
                  {s.error && (
                    <div className="pipeline-row__error text-sm">
                      <span
                        style={{ color: "var(--color-danger)" }}
                        role="alert"
                      >
                        {s.error}
                      </span>
                    </div>
                  )}
                  {s.warnings.length > 0 && (
                    <div className="pipeline-row__warnings">
                      <WarningPanel warnings={s.warnings} />
                    </div>
                  )}
                  <div className="pipeline-row__action" style={{ display: "flex", gap: "4px" }}>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => void stageFns[stage]()}
                      disabled={isAnyRunning}
                      aria-label={`Run: ${STAGE_LABELS[stage]}`}
                    >
                      {s.status === "running" ? "Running…" : "Run"}
                    </button>
                    {s.status !== "idle" && s.status !== "running" && (
                      <button
                        type="button"
                        className="btn btn--secondary btn--sm"
                        disabled={downloading === stage}
                        onClick={async () => {
                          setDownloading(stage);
                          try {
                            const data = await fetchStageOutput(
                              websiteId,
                              stage,
                              s.runId,
                            );
                            downloadJson(
                              data,
                              `${stage}-${websiteId.slice(0, 8)}.json`,
                            );
                          } finally {
                            setDownloading(null);
                          }
                        }}
                        aria-label={`Download: ${STAGE_LABELS[stage]}`}
                      >
                        {downloading === stage ? "…" : "DL"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-muted text-sm" style={{ marginTop: "12px" }}>
            <strong>partial</strong> = completed with warnings.{" "}
            <strong>skipped</strong> = bypassed due to earlier failure.
          </p>
        </section>
      )}
    </div>
  );
}
